import React from 'react';

/**
 * The pieces the three summary-strip columns (LiveTranscriptBar, NewInquiryBar,
 * LandmineBar) share, so the row reads as one strip. The macOS strip
 * (`LiveTranscriptBarView` and friends), the same on WPF:
 *   title 12 semibold · count pill · a 16-tall rule · "Label:" + preview (11) · chevron
 * in a row padded 16 × 8 with 10 between the groups, the whole row clickable.
 */

/** The collapsed header row. The caller adds its tint and hover. */
export const STRIP_HEADER_CLASS = 'flex cursor-pointer items-center gap-2.5 px-4 py-2 transition-colors';

/** Title and count, 6 apart. */
export const STRIP_TITLE_GROUP_CLASS = 'flex flex-shrink-0 items-center gap-1.5';

/** The count pill's shape; the caller adds its colours. */
export const STRIP_PILL_CLASS = 'rounded-control px-1.5 py-0.5 text-[10px] font-semibold leading-3';

/** "Label:" + one line of preview, truncated. */
export const STRIP_PREVIEW_CLASS = 'flex min-w-0 flex-1 items-center gap-1 text-[11px]';

/** The dropdown: its own third wide, white, floating below the strip. */
export const STRIP_DROPDOWN_CLASS = 'absolute left-0 right-0 top-full z-50 border-t border-hairline bg-surface shadow-lg';

/** The dropdown's count line, under a hairline. */
export const STRIP_FOOTER_CLASS = 'border-t border-hairline bg-surface py-1.5 text-center text-[11px] text-ink-secondary';

/** A dropdown's empty state: what is missing, then where it will come from. */
export const StripEmptyState: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div className="space-y-2 p-6 text-center">
    <p className="text-[13px] font-medium text-ink">{title}</p>
    <p className="text-[11px] text-ink-muted">{detail}</p>
  </div>
);

/** The 1 × 16 rule between the title group and the preview. */
export const StripRule: React.FC = () => <span aria-hidden="true" className="h-4 w-px flex-shrink-0 bg-hairline" />;

/** Chevron down while closed, up while open (10). Colour from the caller's text colour. */
export const StripChevron: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => (
  <svg
    aria-hidden="true"
    className={`h-2.5 w-2.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
  </svg>
);
